import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Quote, Star, MapPin, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import Disclaimer from "../components/Disclaimer";
import { CallCard } from "../components/PhoneCTA";

const ORIGIN = "https://medicalpeptides.ai";

/**
 * Service-category taxonomy mapped to display labels. Keep in sync with the
 * options offered in the admin testimonial editor. Any category not in this
 * map still renders — with the raw slug title-cased — so the page is safe
 * against DB drift.
 */
const CATEGORY_LABELS = {
  weight_management: "Weight Management",
  hormone_optimization: "Hormone Optimization",
  peptide_therapy: "Peptide Therapy",
  longevity: "Longevity & Wellness",
  aesthetic: "Aesthetic",
  recovery: "Recovery & Performance",
  hair_restoration: "Hair Restoration",
  sexual_health: "Sexual Health",
  general: "General Care",
};

/** Convert unknown category slug → title-cased human label. */
function labelFor(slug) {
  if (!slug) return "General Care";
  if (CATEGORY_LABELS[slug]) return CATEGORY_LABELS[slug];
  return String(slug).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Small star row for the rating display. */
function RatingStars({ value }) {
  const n = typeof value === "number" ? Math.max(0, Math.min(5, value)) : 0;
  if (!n) return null;
  return (
    <div className="flex items-center gap-0.5" aria-label={`Rated ${n} of 5`} data-testid="testimonial-rating">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={`star-${i}`}
          size={13}
          className={i < n ? "text-[color:var(--vv-gold-deep)] fill-[color:var(--vv-gold)]" : "text-stone-300"}
          fill={i < n ? "currentColor" : "none"}
        />
      ))}
    </div>
  );
}

function stripHtml(s) {
  return (s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

export default function Testimonials() {
  const [items, setItems] = useState(null);   // null while loading, [] after load
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");

  useEffect(() => {
    api.get("/testimonials", { params: { limit: 200 } })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));
  }, []);

  // Derive filter options from data actually present
  const allCategories = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((t) => t.service_category).filter(Boolean))).sort();
  }, [items]);

  const allLocations = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((t) => t.clinic_location).filter(Boolean))).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const needle = q.trim().toLowerCase();
    return items.filter((t) => {
      if (category && t.service_category !== category) return false;
      if (location && t.clinic_location !== location) return false;
      if (needle) {
        const hay = [
          t.display_name, t.clinic_location, t.service_category,
          stripHtml(t.published_text),
        ].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [items, q, category, location]);

  // First-approved testimonial (approved_at desc from API) → hero feature.
  // Only shown when the user has not narrowed the list with search/filters —
  // otherwise it can look mismatched against a filtered grid.
  const filterActive = Boolean(q.trim() || category || location);
  const featured = !filterActive && items && items.length > 0 ? items[0] : null;
  const rest = filtered.filter((t) => !featured || t.id !== featured.id);

  const isLoading = items === null;
  const hasContent = items && items.length > 0;

  // -------- SEO / JSON-LD ------------------------------------------------
  const pageTitle = "Patient Testimonials";
  const pageDesc = "Read approved patient testimonials about consultation experiences at VidaVital Medical. Every testimonial is submitted with documented written consent, reviewed by our medical team, and reflects that patient's individual experience.";

  const orgReviewSchema = hasContent ? {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": `${ORIGIN}/#organization`,
    name: "VidaVital Medical",
    url: ORIGIN,
    review: items.slice(0, 20).map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.display_name || "Verified Patient" },
      datePublished: (t.approved_at || t.date_received || "").slice(0, 10),
      reviewBody: stripHtml(t.published_text).slice(0, 900),
      reviewRating: t.rating ? {
        "@type": "Rating",
        ratingValue: String(t.rating),
        bestRating: "5",
        worstRating: "1",
      } : undefined,
    })),
  } : null;

  return (
    <div data-testid="testimonials-page">
      <SEO
        title={pageTitle}
        description={pageDesc}
        path="/testimonials"
        breadcrumbs={[{ name: "Testimonials", path: "/testimonials" }]}
        schema={orgReviewSchema ? [orgReviewSchema] : undefined}
      />

      {/* Hero */}
      <section className="vv-hero">
        <div className="vv-container py-14">
          <Breadcrumbs items={[{ name: "Testimonials", path: "/testimonials" }]} />
          <div className="vv-hero-eyebrow mb-2">In our patients' words</div>
          <h1 className="font-display text-4xl md:text-5xl">Patient Testimonials</h1>
          <p className="mt-4 text-stone-600 max-w-3xl leading-relaxed">
            Every testimonial on this page was submitted voluntarily with documented written consent
            and reviewed by our medical team before publication. Each reflects one patient's
            individual experience. Results, timelines, and clinical suitability vary — always
            consult a licensed medical provider about your own care.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-stone-200 px-3 py-1.5 text-stone-700">
              <ShieldCheck size={14} className="text-[color:var(--vv-teal)]" /> Written consent required
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-stone-200 px-3 py-1.5 text-stone-700">
              <ShieldCheck size={14} className="text-[color:var(--vv-teal)]" /> Reviewer-approved
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-stone-200 px-3 py-1.5 text-stone-700">
              <ShieldCheck size={14} className="text-[color:var(--vv-teal)]" /> Withdrawn anytime by request
            </span>
          </div>
        </div>
      </section>

      {/* Featured testimonial */}
      {hasContent && featured ? (
        <section className="vv-container -mt-6 mb-8">
          <FeaturedCard t={featured} />
        </section>
      ) : null}

      {/* Filters */}
      <section className="vv-container pb-6">
        <div className="vv-card p-4 grid md:grid-cols-[1fr_220px_220px] gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="testimonials-search"
              placeholder="Search testimonials…"
              className="w-full bg-white border border-stone-200 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[color:var(--vv-teal)]"
            />
          </div>
          <select
            data-testid="testimonials-category-filter"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm"
            aria-label="Filter by service category"
          >
            <option value="">All services</option>
            {allCategories.map((c) => (
              <option key={c} value={c}>{labelFor(c)}</option>
            ))}
          </select>
          <select
            data-testid="testimonials-location-filter"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="bg-white border border-stone-200 rounded-full px-4 py-2 text-sm"
            aria-label="Filter by clinic location"
          >
            <option value="">All locations</option>
            {allLocations.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        {hasContent ? (
          <div className="text-xs text-stone-500 mt-4" data-testid="testimonials-count">
            Showing {filtered.length} of {items.length} testimonials.
          </div>
        ) : null}
      </section>

      {/* Grid */}
      <section className="vv-container pb-16">
        {isLoading ? (
          <div className="vv-card p-8 text-center text-stone-500" data-testid="testimonials-loading">
            Loading testimonials…
          </div>
        ) : !hasContent ? (
          <EmptyState />
        ) : rest.length === 0 && filtered.length === 0 ? (
          <div className="vv-card p-8 text-center text-stone-500" data-testid="testimonials-no-results">
            No testimonials match those filters. <button className="underline hover:text-stone-800" onClick={() => { setQ(""); setCategory(""); setLocation(""); }}>Clear filters</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((t) => <TestimonialCard key={t.id} t={t} />)}
          </div>
        )}
      </section>

      {/* Related content + CTA */}
      <section className="vv-container pb-16">
        <div className="grid md:grid-cols-[1fr_320px] gap-6">
          <div className="grid md:grid-cols-3 gap-4">
            <RelatedTile
              title="Meet the providers"
              body="Board-certified clinicians licensed in your state."
              to="/providers"
              testId="testimonials-related-providers"
            />
            <RelatedTile
              title="Explore treatment goals"
              body="Weight management, hormone optimization, longevity and more."
              to="/treatment-goals"
              testId="testimonials-related-goals"
            />
            <RelatedTile
              title="Educational articles"
              body="Read peptide science summaries reviewed by our medical team."
              to="/research"
              testId="testimonials-related-articles"
            />
          </div>
          <CallCard />
        </div>

        <div className="mt-8"><Disclaimer /></div>

        <div className="mt-8 vv-card p-6 text-sm text-stone-600 leading-relaxed" data-testid="testimonials-disclaimer">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">About these testimonials</div>
          <p>
            Testimonials on this page reflect the individual experiences of specific patients and are
            not a guarantee, promise, or representation of results any other person will achieve.
            Every testimonial is submitted voluntarily with documented written consent and is reviewed
            by a member of our medical team before publication. Patients may withdraw their
            testimonial at any time. Nothing on this page constitutes medical advice — always consult
            a licensed medical provider about your individual care.
          </p>
        </div>
      </section>
    </div>
  );
}

// ---------- Presentational components -------------------------------------

function FeaturedCard({ t }) {
  return (
    <article
      data-testid={`testimonial-featured-${t.id}`}
      className="relative overflow-hidden rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white p-8 md:p-10"
    >
      <div className="absolute top-6 right-6 text-[color:var(--vv-gold-deep)]/25">
        <Quote size={72} strokeWidth={1.5} />
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--vv-gold-deep)]">
        Featured patient story
      </div>
      <blockquote className="mt-4 font-display text-2xl md:text-3xl text-stone-900 leading-snug max-w-3xl">
        “{stripHtml(t.published_text)}”
      </blockquote>
      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="font-semibold text-stone-900">{t.display_name || "Verified Patient"}</span>
        {t.clinic_location ? (
          <span className="inline-flex items-center gap-1 text-stone-600">
            <MapPin size={13} /> {t.clinic_location}
          </span>
        ) : null}
        <span className="inline-block text-[10px] uppercase tracking-widest bg-amber-100 text-amber-900 border border-amber-200 rounded-full px-2 py-0.5">
          {labelFor(t.service_category)}
        </span>
        <RatingStars value={t.rating} />
      </div>
    </article>
  );
}

function TestimonialCard({ t }) {
  return (
    <article
      data-testid={`testimonial-card-${t.id}`}
      className="vv-card p-6 flex flex-col h-full transition-transform duration-200 hover:-translate-y-0.5"
    >
      <div className="text-[color:var(--vv-gold-deep)]/60"><Quote size={20} strokeWidth={2} /></div>
      <p className="mt-3 text-stone-800 leading-relaxed">
        {stripHtml(t.published_text)}
      </p>
      <div className="mt-5 pt-4 border-t border-stone-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-stone-900 truncate">{t.display_name || "Verified Patient"}</div>
          {t.clinic_location ? (
            <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1">
              <MapPin size={11} /> {t.clinic_location}
            </div>
          ) : null}
        </div>
        <RatingStars value={t.rating} />
      </div>
      <div className="mt-3">
        <span className="inline-block text-[10px] uppercase tracking-widest bg-amber-50 text-amber-900 border border-amber-200 rounded-full px-2 py-0.5">
          {labelFor(t.service_category)}
        </span>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div className="vv-card p-10 text-center" data-testid="testimonials-empty">
      <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 border border-amber-200 grid place-items-center text-[color:var(--vv-gold-deep)] mb-4">
        <Quote size={22} />
      </div>
      <div className="font-display text-2xl text-stone-900">Patient testimonials coming soon</div>
      <p className="mt-3 text-stone-600 max-w-xl mx-auto">
        We only publish testimonials once patients complete our written-consent workflow and our
        medical team has reviewed the language. New approved stories will appear here as they are
        submitted.
      </p>
      <Link to="/request-consultation" className="vv-btn-primary mt-6 inline-flex">Schedule a consultation</Link>
    </div>
  );
}

function RelatedTile({ title, body, to, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="vv-card p-5 block hover:border-[color:var(--vv-gold)] transition-colors"
    >
      <div className="font-display text-lg text-stone-900">{title}</div>
      <p className="mt-2 text-sm text-stone-600 leading-relaxed">{body}</p>
      <div className="mt-3 text-xs uppercase tracking-widest text-[color:var(--vv-gold-deep)]">Explore →</div>
    </Link>
  );
}
